import { useMutation, useQueryClient } from '@tanstack/react-query';
import { addLabel, GPanelContactsKeys } from '../../../gpanel-contacts-api';
import * as analytics from '../../../../../../shared/analytics/analytics.json';
import {
  GPanelContactsPermissions,
  hasPermission,
} from '../../../../../../utils/roles/client-roles';
import ChangeLogDialog from '../../../../../../shared/change-log-dialog';
import axios from 'axios';
import { useState } from 'react';
import { ApiException } from '../../../../../../../types';
import { useAnalytics } from '../../../../../../shared/analytics/create-events';
import { ModuleList } from '../../../../../../shared/analytics/module-list';
import SharedDialog from '../../../../../../shared/shared-dialog';
import { handleErrors } from '../../../../../../utils/errors';
import { useStore } from '../../../../../state/zustand-store';
import { useCheckLabelNameCollision } from './use-check-label-collision';
import { Messages } from '../../../../../../utils/message-constants';
import { useUserSettings } from '../../../../../../utils/use-user-settings';
import TextField from '@mui/material/TextField';

export default function GpanelContactsAddLabelDialog({
  handleClose,
}: {
  handleClose: (id?: number, name?: string) => void;
}) {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const setSnackbar = useStore((state) => state.setSnackbar);
  const [errorText, setErrorText] = useState<string | null>(null);
  const { changeLogRoles } = useUserSettings();
  const [labelName, setLabelName] = useState('');

  useCheckLabelNameCollision(labelName, '', setErrorText);

  const { mutate, isPending } = useMutation({
    mutationFn: () => addLabel(labelName, changeLogMessage),
    onSuccess: (data) => {
      setChangeLogMessage('');
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Created('Label'),
      });
      queryClient.setQueryData([GPanelContactsKeys.LabelGroups], data.group);
      queryClient.invalidateQueries({
        queryKey: [GPanelContactsKeys.GPanelLabels],
      });
      handleClose(data.updated[0].id, data.updated[0].name);
    },
    onError: (error) => {
      if (axios.isAxiosError(error)) {
        const errorResponse = error.response?.data as ApiException;
        if (errorResponse.message) {
          setErrorText(errorResponse.message);
        } else {
          handleErrors({
            error,
            snackbarMessage: Messages.Errors.Network.Creating('Label'),
          });
        }
      }
    },
    onSettled: () => {
      createEvent(analytics.directory.gpanel_contacts.create_label, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.gpanelContacts,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    const permission = GPanelContactsPermissions.CreateGpanelLabels.value;

    if (hasPermission(changeLogRoles, permission)) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <>
      <SharedDialog
        open
        handleClose={handleClose}
        title="Create Label"
        dialogContent={
          <TextField
            label="Label Name"
            autoFocus
            fullWidth
            autoComplete="off"
            placeholder="e.g. VIP Clients"
            error={errorText !== null}
            helperText={errorText}
            value={labelName}
            onChange={(event) => setLabelName(event.target.value)}
          />
        }
        primaryButton={{
          title: 'Create Label',
          color: 'primary',
          action: handleSubmit,
          loading: isPending,
          disabled: labelName.length === 0 || errorText !== null,
        }}
      />
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={mutate}
        mutateLoading={isPending}
      />
    </>
  );
}
